import { Component, OnDestroy, OnInit } from '@angular/core';
import { PageEvent } from 'src/app/shared/directives/paginator.component';
import { Observable, Subject, of, filter } from 'rxjs';
import { catchError, finalize, map, mergeMap, switchMap, takeUntil, tap, toArray } from 'rxjs/operators';
import { AccountService } from 'src/app/shared/services/account.service';
import { DataService } from 'src/app/shared/services/data.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { Allocation, TodayAllocationPresentation } from '../models/today-allocation';
import { mockAllocations } from './mockAllocations';
import { AllocationService } from 'src/app/shared/services/allocation.service';
import { UndoAllocationRequest } from '../models/undo-allocation-request';
import { NgxSpinnerService } from 'ngx-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { MessagesService } from 'src/app/components/messages/messages.service';
import { DialogInputComponent } from 'src/app/shared/components/dialog-input/dialog-input.component';
import { DialogInputConfig, DialogResult } from 'src/app/shared/models/dialog-input';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { faRotate, faPlus, faMinus, faScaleBalanced } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'app-view-allocations',
    templateUrl: './view-allocations.component.html',
    styleUrls: ['./view-allocations.component.scss'],
    standalone: false
})
export class ViewAllocationsComponent implements OnInit, OnDestroy {
  faRotate: any = faRotate;
  faPlus: any = faPlus;
  faMinus: any = faMinus;
  faScaleBalanced: any = faScaleBalanced;

  displayedColumns: string[] = ['allocationId', 'symbol', 'side', 'avgPrice', 'totalQuantity', 'totalValue', 'bowiBrokerId', 'undo'];
  public allocationDetailsColumns: string[] = ['accountId', 'symbolDetail', 'sideDetail', 'price', 'quantityDetail'];
  public allocationOrdersColumns: string[] = ['orderId', 'symbol', 'side', 'execPrice', 'execQuantity'];
  private onDestroy$ = new Subject<void>();
  public loading: boolean = true;

  totalRecords = 0;
  pagedElements$: Observable<TodayAllocationPresentation[]>;
  public todaysAllocation: TodayAllocationPresentation[] = [];

  pageIndex = 0;
  pageSize = 10;
  searchExecuted = false;
  private riaID: number;
  private useMockData = false; // Flag to toggle between mock data and real data

  constructor(
    private data: DataService,
    private accountService: AccountService,
    private utilsService: UtilsService,
    private allocationService: AllocationService,
    private spinnerService: NgxSpinnerService,
    private messageService: MessagesService,
    public dialog: DialogService
  ) { }

  ngOnInit(): void {
    this.loadAllocations();
  }

  loadAllocations(): void {
    this.loading = true;
    
    // Follow the same pattern as AllocationListComponent
    this.data.currentRiaId$
      .pipe(
        filter(riaId => riaId > 0), // Ensure riaId is valid
        switchMap(riaId => {
          this.riaID = riaId; // Store riaId for later use
          // Choose data source based on flag
          const dataSource$ = this.useMockData ? 
            of(mockAllocations) : 
            this.accountService.getHistorySummary(riaId);
          
          return dataSource$.pipe(
            switchMap(allocations => this.handleAllocationData(riaId, allocations))
          );
        }),
        catchError(error => this.handleFetchError(error)),
        tap(() => this.loading = false),
        takeUntil(this.onDestroy$)
      )
      .subscribe((processedAllocations: TodayAllocationPresentation[]) => {
        this.updateAllocations(processedAllocations);
      });
  }

  private handleAllocationData(riaId: number, todaysAllocation: Allocation[]): Observable<TodayAllocationPresentation[]> {
    if (!todaysAllocation || todaysAllocation.length === 0) {
      this.searchExecuted = true;
      return of([]);
    }
    return this.processAllocations(todaysAllocation);
  }

  private updateAllocations(processedAllocations: TodayAllocationPresentation[]): void {
    this.todaysAllocation = processedAllocations.map(allocation => ({
      ...allocation,
      expanded: false
    }));

    this.totalRecords = processedAllocations.length;
    this.getPagedData();
    this.searchExecuted = true;
  }

  private handleFetchError(error: any): Observable<TodayAllocationPresentation[]> {
    console.error("Error fetching allocations:", error);
    this.searchExecuted = true;
    return of([]);
  }

  processAllocations(allocations: Allocation[]): Observable<TodayAllocationPresentation[]> {
    return of(...allocations).pipe(
      mergeMap(allocation => this.processElement(allocation)),
      toArray()
    );
  }

  processElement(element: Allocation): Observable<TodayAllocationPresentation> {
    const allocationWithUser: TodayAllocationPresentation = { ...element, user: '', expanded: false };

    if (element.bowiBrokerId > 0) {
      return this.utilsService.fetchBrokerUserDetails(element.bowiBrokerId)
        .pipe(
          tap((user) => {
            const userType = "Broker";
            allocationWithUser.user = user && user.UserName ? `${userType} - ${user.UserName}` : `${userType} - Unknown`;
          }),
          map(() => allocationWithUser),
          catchError(() => of(allocationWithUser)) // Return the element with default user on error
        );
    } else {
      return this.utilsService.fetchClientUserDetails(this.riaID)
        .pipe(
          tap((user) => {
            allocationWithUser.user = user && user.userID ? `Client - ${user.userID}` : '';
          }),
          map(() => allocationWithUser),
          catchError(() => of(allocationWithUser)) // Return the element with default user on error
        );
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  getPagedData(): void {
    this.pagedElements$ = of(this.todaysAllocation.slice(this.pageSize * this.pageIndex, (this.pageSize * this.pageIndex) + this.pageSize));
  }

  pageChangeEvent(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.getPagedData();
  }

  undoAllocation($event: Event, allocation: TodayAllocationPresentation): void {
    $event.stopPropagation();

    this.openDialog(`Do you want to undo the Allocation Id: <b>${allocation.allocationId}</b> related to the Order # <b>${allocation.orderDetail[0].orderId}</b>?`, "Confirm", "Yes", false,
    () => {
      let undoAllocationRequest: UndoAllocationRequest[] = [];
      allocation.allocationDetails.forEach(element => {
        undoAllocationRequest.push({
          allocationId: element.allocationId.trim(),
          accountId: element.accountId.trim(),
          riaId: this.riaID,
          symbol: element.symbol.trim(),
          side: element.side.trim(),
          price: element.price,
          quantity: element.quantity,
          ecnOrderId: element.ecnOrderId.trim(),
          ecnExecId: element.ecnExecId.trim()
          });
      });
          
      this.spinnerService.show();
      this.allocationService.undoTradeAllocation(undoAllocationRequest).pipe(
          catchError((response: HttpErrorResponse) => {
              this.messageService.showError(`Failed to remove (undo) the allocation. Message: ${response?.message}`, 10000);
              throw response;
          }),
          finalize(()=> this.spinnerService.hide())
      )
      .subscribe(result => {
          this.messageService.showMessage('Undo successfull',5000);     
          this.loadAllocations();
      });
    });    
  }

  openDialog(text: string, alertTitle: string = 'Alert', confirmText: string = 'Ok', isAlertDialog: boolean = true, onConfirm?: Function, onCancel?: Function) {
    let dialogRef;
    let dialogConfig: DialogInputConfig;
    dialogConfig= {
        inputEnabled: false,
        maxInputLength: 200,
        title: alertTitle,
        confirmText: confirmText,
        isAlertDialog,
        text
    };

    dialogRef = this.dialog.open(DialogInputComponent, {
      data:dialogConfig,
      disableClose: true,
    });


    return dialogRef.afterClosed().subscribe((result: DialogResult) => {
      if(result){
        if(onConfirm){
            onConfirm();
        } 
      } else {
        if(onCancel){
            onCancel();
        } 
      }
    });
  }

  public toggleRow(element: TodayAllocationPresentation): void {
    element.expanded = !element.expanded;
  }

  public checkIfAnyOrderChecked(element: any) {
    // Implementation if needed
  }

  public parentCheckboxChanged(element) {
    // Implementation if needed
  }

  public checkboxChanged(element, $event) {
    // Implementation if needed
  }

  allocateSelected(_t99: any) {
    throw new Error('Method not implemented.');
  }
}
